/**
 * Web Search Factory
 * Creates appropriate web search adapters and builds context for LLM
 */

import type { IWebSearchAdapter } from './IWebSearchAdapter';
import type { AgentWebSearchConfig, WebSearchResult } from '@/types/webSearch.types';
import { TavilyAdapter } from './tavilyAdapter';
import { BingAdapter } from './bingAdapter';
import { GoogleAdapter } from './googleAdapter';
import { SerpApiAdapter } from './serpApiAdapter';
import { BraveAdapter } from './braveAdapter';

/**
 * Factory to create appropriate web search adapter based on config
 * @param config Agent's web search configuration
 * @returns Initialized adapter or null if disabled/invalid
 */
export function createWebSearchAdapter(
  config: AgentWebSearchConfig | undefined
): IWebSearchAdapter | null {
  if (!config || config.provider === 'none' || !config.enabled) {
    return null;
  }

  switch (config.provider) {
    case 'tavily':
      return new TavilyAdapter();
    case 'bing':
      return new BingAdapter();
    case 'google':
      return new GoogleAdapter();
    case 'serpapi':
      return new SerpApiAdapter();
    case 'brave':
      return new BraveAdapter();
    default:
      // This should never happen due to TypeScript's exhaustive checking
      console.error('Unknown search provider');
      return null;
  }
}

/**
 * Build context string from web search results
 * Formats results for inclusion in LLM prompt
 * Similar to buildContextFromSearchResults in graphService.ts
 *
 * @param results Array of normalized web search results
 * @param includeAnswer Optional AI-generated answer (Tavily-specific)
 * @returns Formatted context string for LLM
 */
export function buildContextFromWebSearchResults(
  results: WebSearchResult[],
  includeAnswer?: string
): string {
  if (results.length === 0) {
    return '';
  }

  let context = 'Web Search Results:\n\n';

  // Include AI-generated answer if provided (Tavily feature)
  if (includeAnswer) {
    context += `AI Summary: ${includeAnswer}\n\n`;
    context += 'Detailed Sources:\n\n';
  }

  // Format each result with citation number
  results.forEach((result, index) => {
    const citationNumber = index + 1;
    context += `[Web ${citationNumber}] ${result.title}\n`;
    context += `URL: ${result.url}\n`;
    context += `${result.snippet}\n`;

    if (result.publishedDate) {
      context += `Published: ${result.publishedDate.toLocaleDateString()}\n`;
    }

    if (result.relevanceScore !== undefined) {
      context += `Relevance: ${(result.relevanceScore * 100).toFixed(0)}%\n`;
    }

    context += '\n';
  });

  return context;
}

/**
 * Build context with truncation for large result sets
 * @param results Array of normalized web search results
 * @param includeAnswer Optional AI-generated answer
 * @param maxLength Maximum context length in characters
 * @returns Truncated context string
 */
export function buildTruncatedContext(
  results: WebSearchResult[],
  includeAnswer?: string,
  maxLength: number = 8000
): string {
  let context = buildContextFromWebSearchResults(results, includeAnswer);

  if (context.length > maxLength) {
    // Truncate results to fit within maxLength
    const truncatedResults = results.slice(0, Math.max(1, Math.floor(results.length / 2)));
    context = buildContextFromWebSearchResults(truncatedResults, includeAnswer);

    if (context.length > maxLength) {
      // Still too long, truncate the actual text
      context = context.substring(0, maxLength - 100) + '\n\n[Context truncated due to length]';
    }
  }

  return context;
}
